import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { PublicClientApplication } from "@azure/msal-browser";
import { loadAppConfig } from "@shell/auth/authConfig";
import { createMsalInstance } from "@shell/auth/msalInstance";
import { bootstrapAuth } from "@shell/auth/bootstrapAuth";
import { installGlobalStyles } from "@tokens/cssVars";
import { initializeTheme } from "@tokens/theme";
import { initializeMotionPreference } from "@tokens/motionPreference";
import { serviceWorkerUpdates } from "@shell/pwa";
import { App } from "./App";

// Apply design tokens synchronously *before* React mounts to prevent FOUC
// (flashes of unstyled white inputs/surfaces on first paint).
initializeTheme(document.documentElement);
// Restore the stored motion preference before the first transition can run.
initializeMotionPreference(document.documentElement);
// Install @property registrations, focus-visible discipline, and the
// reduced-motion structural override exactly once. Idempotent.
installGlobalStyles();

const rootEl = document.getElementById("root");
if (!rootEl) {
    throw new Error("Root element #root not found in index.html");
}

const config = loadAppConfig();
const msalInstance: PublicClientApplication = createMsalInstance(config);
await msalInstance.initialize();

// Consume any returning redirect, re-establish a cached account, or attempt a
// silent SSO renewal against a live Entra session — all before React mounts.
// Total by contract: on no usable session the app renders the sign-in gate.
await bootstrapAuth(msalInstance, config);

createRoot(rootEl).render(
    <StrictMode>
        <App msalInstance={msalInstance} config={config} />
    </StrictMode>,
);

// Update flow (registerType "prompt"): a newer worker installs and waits;
// `onNeedRefresh` raises the in-app toast; the user's Reload sends
// SKIP_WAITING and the page reloads once the new worker controls it. No tab
// reloads on its own when a deploy lands.
if (import.meta.env.PROD) {
    void import("virtual:pwa-register").then(({ registerSW }) => {
        const updateSW = registerSW({
            immediate: true,
            onNeedRefresh: () => {
                serviceWorkerUpdates.signalUpdateReady(() => {
                    void updateSW(true);
                });
            },
        });
    });
}
